import classNames from 'classnames';
import type { CategoryCatalog, ProductCatalog, StrapiListRequest, StrapiListResponse } from '@/interfaces/strapi';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useStrapiApi, useStrapiParams } from '@/hooks';
import { getCatalogProducts } from '@/services/strapi';
import { CatalogProductListItem } from '@/components';
import CTNPagination from '@ctn/ui/components/ctn-pagination';

type CatalogProductListProps = {
	readonly className?: string;
	readonly category: CategoryCatalog;
	readonly initialPage?: number;
	readonly pageSize?: number;
};

const classNameComponent = 'c-cnm-catalog-products-list';

export function CatalogProductList({ className, category, initialPage = 1, pageSize = 8 }: CatalogProductListProps) {
	const wrapperClassName = classNames(classNameComponent, className);
	const [page, setPage] = useState(initialPage);

	const additionalFilters = useMemo(
		() => ({
			categoria: { slug: { $eq: category.slug } },
		}),
		[category.slug],
	);

	const params = useStrapiParams({
		page,
		pageSize,
		additionalFilters,
		populate: {
			image: true,
			categoria: true,
		},
	});

	const { data, loading } = useStrapiApi<StrapiListResponse<ProductCatalog>, StrapiListRequest>(getCatalogProducts, {
		autoFetch: true,
		params,
	});

	const products = data?.list ?? [];
	const pagination = data?.pagination;
	const paginationRef = useRef<any>(null);
	const totalPages = pagination?.pageCount ?? 1;

	useEffect(() => {
		if (pagination && paginationRef.current?.goToPage) {
			paginationRef.current.goToPage(pagination.page ?? page);
		}
		if (pagination && typeof pagination.page === 'number' && pagination.page !== page) {
			setPage(pagination.page);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagination?.page]);

	const handlePageChange = (newPage: number) => {
		if (newPage === page) return;
		setPage(newPage);
	};

	return (
		<section className={wrapperClassName}>
			<h3 className={`${classNameComponent}__title u-mb-16`}>{category.name}</h3>

			{loading && <p>Cargando {category.name}...</p>}

			{!loading && products.length === 0 && <p>No hay productos en esta categoría.</p>}

			{!loading && products.length > 0 && (
				<>
					<ul className={`${classNameComponent}__grid`}>
						{products.map((p) => (
							<li key={p.id}>
								<CatalogProductListItem product={p} />
							</li>
						))}
					</ul>

					{pagination && totalPages > 1 && (
						<nav className="c-pagination u-mt-16" aria-label={`Paginación ${category.name}`}>
							<CTNPagination
								ref={paginationRef}
								pageSize={pageSize}
								totalData={pagination.total ?? 0}
								locales={{
									text1: 'Página',
									text2: 'de',
									text3: 'Mostrando registros {0} de {1}',
								}}
								onPageChange={handlePageChange}
							/>
						</nav>
					)}
				</>
			)}
		</section>
	);
}
